import { makeObservable, observable, action } from 'mobx';
import { Subscription, Plan } from '../model/professional/subscription-model';

/**
 * Store pour gérer l'état des abonnements
 */
class SubscriptionStore {
  @observable currentSubscription: Subscription | null = null;
  @observable availablePlans: Plan[] = [];
  @observable isLoading: boolean = false;

  constructor() {
    makeObservable(this);
  }

  @action
  setCurrentSubscription(subscription: Subscription | null) {
    this.currentSubscription = subscription;
  }

  @action
  setAvailablePlans(plans: Plan[]) {
    this.availablePlans = plans;
  }

  @action
  setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  /**
   * Récupère le plan actuel de l'utilisateur
   */
  get getCurrentPlan(): Plan | null {
    if (!this.currentSubscription) return null;
    return this.availablePlans.find(p => p.id === this.currentSubscription!.planName) || null;
  }

  /**
   * Vérifie si l'utilisateur a une fonctionnalité activée
   */
  hasFeature(featureName: keyof Subscription): boolean {
    if (!this.currentSubscription) return false;
    const featureValue = this.currentSubscription[featureName];
    return typeof featureValue === 'boolean' ? featureValue : false;
  }

  /**
   * Récupère la limite d'une ressource
   */
  getLimit(limitName: 'maxCompanies' | 'maxCollaborators' | 'maxActivePromoCodes'): number {
    if (!this.currentSubscription) return 0;
    return this.currentSubscription[limitName] || 0;
  }

  /**
   * Vérifie si l'abonnement est actif
   */
  get isActive(): boolean {
    return this.currentSubscription?.status === 'active';
  }

  /**
   * Vérifie si l'abonnement est en période d'essai
   */
  get isTrialing(): boolean {
    return this.currentSubscription?.status === 'trialing';
  }

  /**
   * Vérifie si l'abonnement est annulé (mais encore actif jusqu'à la fin de la période)
   */
  get isCanceled(): boolean {
    return this.currentSubscription?.cancelAtPeriodEnd || false;
  }
}

// Exporter une instance singleton du store
const subscriptionStore = new SubscriptionStore();
export default subscriptionStore;
